import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  WarningAmber as WarnIcon,
  AutoFixHigh as AutoFixIcon,
  Refresh as RefreshIcon,
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Admin page — "Orphaned Route Stops."
 *
 * Lists every active `job_site` whose `quote_id` is NULL. Without a quote,
 * the stop has no services, no pricing, and no property info, so the crew
 * shows up blind. These are almost always legacy rows from before we made
 * `quote_id` mandatory on the POST endpoint.
 *
 * Admins can:
 *   - "Auto-link single-quote customers" — bulk fix the common case.
 *   - Pick a quote for each remaining orphan manually.
 *   - See which customers have zero eligible quotes (need a quote first).
 */
const OrphanedJobSites = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rows, setRows] = useState([]);
  const [autoRunning, setAutoRunning] = useState(false);

  const [pickOpen, setPickOpen] = useState(false);
  const [pickRow, setPickRow] = useState(null);
  const [linkingQuoteId, setLinkingQuoteId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await makeApiCall('/api/routes/orphaned-job-sites');
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('orphanedJobSites.failedToLoad'));
      }
      const data = await res.json();
      setRows(data.orphaned_job_sites || []);
    } catch (err) {
      setError(err.message || t('orphanedJobSites.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const runAutoFix = async () => {
    try {
      setAutoRunning(true);
      setError('');
      const res = await makeApiCall('/api/routes/backfill-orphaned-quotes', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('orphanedJobSites.autoFixFailed'));
      setSuccess(t('orphanedJobSites.autoFixResult', {
        linked: data.linked || 0,
        ambiguous: data.ambiguous || 0,
        noQuote: data.no_quote || 0,
      }));
      await load();
    } catch (err) {
      setError(err.message || t('orphanedJobSites.autoFixFailed'));
    } finally {
      setAutoRunning(false);
    }
  };

  const openPick = (row) => {
    setPickRow(row);
    setPickOpen(true);
  };
  const closePick = () => {
    if (linkingQuoteId) return;
    setPickRow(null);
    setPickOpen(false);
  };

  const linkQuote = async (quote) => {
    if (!pickRow || !quote) return;
    try {
      setLinkingQuoteId(quote.id);
      setError('');
      const res = await makeApiCall(`/api/routes/job-sites/${pickRow.id}/link-quote`, {
        method: 'PATCH',
        body: JSON.stringify({ quote_id: quote.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('orphanedJobSites.linkFailed'));
      setSuccess(t('orphanedJobSites.linkedOne'));
      setPickOpen(false);
      setPickRow(null);
      await load();
    } catch (err) {
      setError(err.message || t('orphanedJobSites.linkFailed'));
    } finally {
      setLinkingQuoteId(null);
    }
  };

  const rowLabel = (r) => r.company_name
    || `${r.first_name || ''} ${r.last_name || ''}`.trim()
    || `#${r.customer_id || r.id}`;

  const dayLabel = (dow) => {
    if (!dow || dow < 1 || dow > 7) return '\u2014';
    return t(`days.${DAY_KEYS[dow - 1]}`);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <WarnIcon color="warning" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('orphanedJobSites.title')}
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('orphanedJobSites.intro')}
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <Button
          variant="contained"
          color="primary"
          startIcon={autoRunning ? <CircularProgress size={16} color="inherit" /> : <AutoFixIcon />}
          onClick={runAutoFix}
          disabled={autoRunning || loading || rows.length === 0}
          aria-label={t('orphanedJobSites.autoFix')}
        >
          {t('orphanedJobSites.autoFix')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading}
          aria-label={t('common.refresh')}
        >
          {t('common.refresh')}
        </Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {!loading && rows.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <LinkIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
          <Typography>{t('orphanedJobSites.allClean')}</Typography>
        </Paper>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('orphanedJobSites.title')}>
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('orphanedJobSites.customer')}</strong></TableCell>
                <TableCell><strong>{t('orphanedJobSites.address')}</strong></TableCell>
                <TableCell><strong>{t('orphanedJobSites.day')}</strong></TableCell>
                <TableCell><strong>{t('orphanedJobSites.truck')}</strong></TableCell>
                <TableCell><strong>{t('orphanedJobSites.eligibleQuotes')}</strong></TableCell>
                <TableCell align="right"><strong>{t('orphanedJobSites.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const eligible = (r.candidate_quotes || []).filter((q) => !q.already_on_route);
                const hasNone = eligible.length === 0;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>{rowLabel(r)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {[r.street_address, r.city].filter(Boolean).join(', ') || '\u2014'}
                      </Typography>
                    </TableCell>
                    <TableCell>{dayLabel(r.day_of_week)}</TableCell>
                    <TableCell>{r.truck_name || '\u2014'}</TableCell>
                    <TableCell>
                      {hasNone ? (
                        <Tooltip title={t('orphanedJobSites.noEligibleHelp')}>
                          <Chip
                            icon={<LinkOffIcon />}
                            label={t('orphanedJobSites.noneEligible')}
                            color="error"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label={t('orphanedJobSites.countEligible', { count: eligible.length })}
                          color={eligible.length === 1 ? 'success' : 'info'}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={hasNone}
                        onClick={() => openPick(r)}
                        aria-label={t('orphanedJobSites.linkQuote')}
                      >
                        {t('orphanedJobSites.linkQuote')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pick a quote dialog */}
      <Dialog
        open={pickOpen}
        onClose={closePick}
        fullWidth
        maxWidth="sm"
        aria-labelledby="orphan-pick-title"
      >
        <DialogTitle id="orphan-pick-title">
          {t('orphanedJobSites.pickQuoteTitle')}
          {pickRow && (
            <Typography variant="caption" color="text.secondary" display="block">
              {rowLabel(pickRow)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            {(pickRow?.candidate_quotes || []).filter((q) => !q.already_on_route).map((q) => {
              const total = q.total_amount != null
                ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'CAD' }).format(Number(q.total_amount))
                : null;
              const isLoading = linkingQuoteId === q.id;
              return (
                <ListItem
                  key={q.id}
                  divider
                  secondaryAction={
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => linkQuote(q)}
                      disabled={!!linkingQuoteId}
                      startIcon={isLoading ? <CircularProgress size={14} /> : <LinkIcon />}
                      aria-label={t('orphanedJobSites.linkThisQuote', { number: q.quote_number || q.id })}
                    >
                      {t('orphanedJobSites.linkThisQuote', { number: q.quote_number || q.id })}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {q.quote_number || `#${q.id}`}
                        </Typography>
                        {q.service_type && <Chip size="small" label={q.service_type} />}
                        {total && <Chip size="small" variant="outlined" label={total} />}
                      </Stack>
                    }
                    secondary={
                      q.created_at ? new Date(q.created_at).toLocaleDateString() : null
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePick} disabled={!!linkingQuoteId}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrphanedJobSites;
